export enum THEME {
  LIGHT = 'light',
  DARK = 'dark',
  SYSTEM = 'system',
}

export type ThemePreference = THEME;

export type ResolvedTheme = THEME.LIGHT | THEME.DARK;

const THEME_COOKIE_NAME = 'APP_THEME';
const THEME_COOKIE_MAX_AGE = 60 * 60 * 24 * 365;

const getThemeCookie = (): string | undefined => {
  return document.cookie
    .split('; ')
    .find((row) => row.startsWith(`${THEME_COOKIE_NAME}=`))
    ?.split('=')
    .slice(1)
    .join('=');
};

export const getThemePreference = (): ThemePreference => {
  const value = getThemeCookie();

  if (
    value === THEME.LIGHT ||
    value === THEME.DARK ||
    value === THEME.SYSTEM
  ) {
    return value;
  }

  return THEME.SYSTEM;
};

export const setThemePreference = (theme: ThemePreference, domain?: string): void => {
  const domainAttribute = domain ? `; domain=${domain}` : '';

  document.cookie = `${THEME_COOKIE_NAME}=${encodeURIComponent(theme)}; path=/${domainAttribute}; max-age=${THEME_COOKIE_MAX_AGE}`;
};

export const removeThemePreference = (domain?: string): void => {
  const domainAttribute = domain ? `; domain=${domain}` : '';

  document.cookie = `${THEME_COOKIE_NAME}=; path=/${domainAttribute}; max-age=0`;
};

export const hasThemePreference = (): boolean => {
  return document.cookie.split('; ').some((row) => row.startsWith(`${THEME_COOKIE_NAME}=`));
};

export const getSystemTheme = (): ResolvedTheme => {
  return window.matchMedia('(prefers-color-scheme: dark)').matches ? THEME.DARK : THEME.LIGHT;
};

export const applyTheme = (theme: ResolvedTheme): void => {
  document.documentElement.dataset.theme = theme;
  document.documentElement.style.colorScheme = theme;
};

export const initializeTheme = (domain?: string): void => {
  if (!hasThemePreference()) {
    setThemePreference(THEME.SYSTEM, domain);
  }

  const preference = getThemePreference();
  const theme = preference === THEME.SYSTEM ? getSystemTheme() : preference;

  applyTheme(theme);
};

export const setTheme = (theme: ThemePreference, domain?: string): void => {
  setThemePreference(theme, domain);

  const resolvedTheme = theme === THEME.SYSTEM ? getSystemTheme() : theme;

  applyTheme(resolvedTheme);
};

export const listenForSystemThemeChanges = (): (() => void) => {
  const mediaQuery = window.matchMedia(
    '(prefers-color-scheme: dark)'
  );

  const handleChange = (event: MediaQueryListEvent): void => {
    if (getThemePreference() !== THEME.SYSTEM) {
      return;
    }

    applyTheme(event.matches ? THEME.DARK : THEME.LIGHT);
  };

  mediaQuery.addEventListener('change', handleChange);

  return () => {
    mediaQuery.removeEventListener('change', handleChange);
  };
};
